import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from "react";
import { SearchLg } from "@untitledui/icons";
import { Link as AriaLink } from "react-aria-components";
import { Input } from "../../../base/input/input";
import { MizbanfaLockup } from "../../../foundations/logo/mizbanfa-logo";
import { cx } from "../../../../utils/cx";
import { MobileNavigationHeader } from "../base-components/mobile-header";
import { NavAccountCard, type NavAccountMenuItem, type NavAccountType } from "../base-components/nav-account-card";
import { NavList, isNavActive } from "../base-components/nav-list";
import type { NavItemDividerType, NavItemType } from "../config";

interface SidebarNavigationProps {
    /** URL of the currently active item. */
    activeUrl?: string;
    /** List of items to display. */
    items: (NavItemType | NavItemDividerType)[];
    /**
     * Secondary links (help, account, system pages): rendered as a compact muted card under `footerCaption`.
     * A `{ divider: true }` entry draws a hairline inside the card, e.g. to set «حساب» apart from the help links.
     */
    footerItems?: (NavItemType | NavItemDividerType)[];
    /** Small label above the footer items, e.g. «راهنما و حساب». */
    footerCaption?: ReactNode;
    /** Feature card to display above the account card. */
    featureCard?: ReactNode;
    /** A card under the main items, in the free space above the footer (scrolls with the items). */
    navCard?: ReactNode;
    /** Brand block at the top; defaults to the Mizbanfa wordmark. */
    logo?: ReactNode;
    /** Small caption under the logo, e.g. the panel name. */
    caption?: ReactNode;
    /** Signed-in account; hides the account card when omitted. */
    account?: NavAccountType;
    /** Items of the account popover menu. */
    accountMenuItems?: NavAccountMenuItem[];
    /** Called by the «خروج» item of the account menu. */
    onSignOut?: () => void;
    /** Show the search input. */
    showSearch?: boolean;
    /** Placeholder of the search input. */
    searchPlaceholder?: string;
    /** Whether to hide the inline-end border. */
    hideBorder?: boolean;
    /** Additional CSS classes to apply to the sidebar. */
    className?: string;
    /** Actions in the mobile header (notification bell, search), before the menu button. */
    mobileActions?: ReactNode;
}

/** One secondary link inside the footer card: 16px icon, 13px label, raised chip when current. */
const SidebarFooterLink = ({ item, current }: { item: NavItemType; current: boolean }) => {
    const Icon = item.icon;
    return (
        <AriaLink
            href={item.href}
            aria-current={current ? "page" : undefined}
            className={cx(
                "group/foot flex min-h-8 w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] outline-focus-ring transition duration-100 ease-linear select-none focus-visible:outline-2 focus-visible:outline-offset-2",
                current
                    ? "bg-primary font-semibold text-primary shadow-xs ring-1 ring-secondary ring-inset"
                    : "font-medium text-tertiary hover:bg-secondary_hover hover:text-secondary_hover",
            )}
        >
            {Icon && (
                <Icon
                    aria-hidden="true"
                    className={cx(
                        "size-4 shrink-0 stroke-[2.25px] transition-inherit-all",
                        current ? "text-fg-brand-primary" : "text-fg-quaternary group-hover/foot:text-fg-quaternary_hover",
                    )}
                />
            )}
            <span className="flex-1 truncate">{item.label}</span>
            {item.badge}
        </AriaLink>
    );
};

/** Help/account links as a quiet card: visibly secondary to the main nav, current page still obvious. */
const SidebarFooterNav = ({ items, caption, activeUrl }: { items: (NavItemType | NavItemDividerType)[]; caption?: ReactNode; activeUrl?: string }) => {
    const captionId = useId();
    return (
        <nav aria-labelledby={caption ? captionId : undefined} aria-label={caption ? undefined : "پیوندهای جانبی"} className="rounded-xl bg-secondary p-1 ring-1 ring-secondary ring-inset">
            {caption && (
                <p id={captionId} className="px-2.5 pt-1.5 pb-1 text-xs font-semibold text-quaternary">
                    {caption}
                </p>
            )}
            <ul className="flex flex-col gap-0.5">
                {items.map((item, index) =>
                    item.divider ? (
                        <li key={`divider-${index}`} role="separator" aria-hidden="true" className="mx-2.5 my-0.5 h-px bg-border-secondary" />
                    ) : (
                        <li key={item.label}>
                            <SidebarFooterLink item={item as NavItemType} current={isNavActive(item.href, activeUrl)} />
                        </li>
                    ),
                )}
            </ul>
        </nav>
    );
};

/**
 * The nav items scroll between the fixed brand on top and the fixed help card and account at the
 * bottom; a soft fade on the top/bottom edge shows there is more to scroll to.
 */
const ScrollingNav = ({ children }: { children: ReactNode }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [edges, setEdges] = useState({ up: false, down: false });
    const measure = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const up = el.scrollTop > 2;
        const down = el.scrollTop + el.clientHeight < el.scrollHeight - 2;
        setEdges((e) => (e.up === up && e.down === down ? e : { up, down }));
    }, []);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        if (el.firstElementChild) ro.observe(el.firstElementChild);
        return () => ro.disconnect();
    }, [measure]);
    const fade = "pointer-events-none absolute inset-x-0 z-10 h-10 transition-opacity duration-200";
    return (
        <div className="relative flex min-h-0 flex-1 flex-col">
            <div aria-hidden="true" className={cx(fade, "top-0 bg-linear-to-b from-(--color-bg-primary) to-transparent", edges.up ? "opacity-100" : "opacity-0")} />
            <div ref={ref} onScroll={measure} className="min-h-0 flex-1 overflow-y-auto overscroll-contain [scrollbar-width:thin]">
                <div>{children}</div>
            </div>
            <div aria-hidden="true" className={cx(fade, "bottom-0 h-14 bg-linear-to-t from-(--color-bg-primary) via-(--color-bg-primary)/70 to-transparent", edges.down ? "opacity-100" : "opacity-0")} />
        </div>
    );
};

/**
 * Untitled UI "Dashboard 01" sidebar (SidebarNavigationSimple), adapted for mfa-aff:
 * RTL-migrated (fixed to the inline-start edge, i.e. the right side in RTL), Persian labels,
 * Mizbanfa logo, and a real account prop instead of placeholder people.
 */
export const SidebarNavigationSimple = ({
    activeUrl,
    items,
    footerItems = [],
    footerCaption,
    featureCard,
    navCard,
    logo,
    caption,
    account,
    accountMenuItems,
    onSignOut,
    showSearch = false,
    searchPlaceholder = "جست‌وجو",
    hideBorder = false,
    className,
    mobileActions,
}: SidebarNavigationProps) => {
    const MAIN_SIDEBAR_WIDTH = 280;

    const brand = logo ?? <MizbanfaLockup caption={caption} />;

    const content = (
        <aside
            style={
                {
                    "--width": `${MAIN_SIDEBAR_WIDTH}px`,
                } as React.CSSProperties
            }
            className={cx(
                "flex h-full w-full max-w-full flex-col overflow-hidden bg-primary pt-5 lg:w-(--width) lg:pt-6",
                !hideBorder && "border-secondary md:border-e",
                className,
            )}
        >
            <div className="flex shrink-0 flex-col gap-5 px-4 lg:px-5">
                <div className="flex items-center">{brand}</div>

                {showSearch && (
                    <>
                        <Input size="md" aria-label={searchPlaceholder} placeholder={searchPlaceholder} icon={SearchLg} className="md:hidden" />
                        <Input size="sm" aria-label={searchPlaceholder} placeholder={searchPlaceholder} icon={SearchLg} className="max-md:hidden" />
                    </>
                )}
            </div>

            <ScrollingNav>
                <NavList activeUrl={activeUrl} items={items} className="pb-3" />
                {/* Short screens: the help card scrolls with the items so they keep room; only the account stays fixed. */}
                {footerItems.length > 0 && (
                    <div className="hidden px-4 pb-3 [@media(max-height:820px)]:block">
                        <SidebarFooterNav items={footerItems} caption={footerCaption} activeUrl={activeUrl} />
                    </div>
                )}
            </ScrollingNav>

            <div className="flex shrink-0 flex-col gap-3 border-t border-secondary/60 px-4 py-3 lg:py-4">
                {footerItems.length > 0 && (
                    <div className="[@media(max-height:820px)]:hidden">
                        <SidebarFooterNav items={footerItems} caption={footerCaption} activeUrl={activeUrl} />
                    </div>
                )}

                {featureCard}

                {/* Kept out of the scrolling list so it is always in sight, however long the menu is. */}
                {navCard}

                {account && <NavAccountCard account={account} menuItems={accountMenuItems} onSignOut={onSignOut} />}
            </div>
        </aside>
    );

    return (
        <>
            {/* Mobile header navigation */}
            <MobileNavigationHeader logo={logo ?? <MizbanfaLockup caption={caption} size="sm" />} actions={mobileActions} activeUrl={activeUrl}>
                {content}
            </MobileNavigationHeader>

            {/* Desktop sidebar navigation (inline-start edge: right in RTL) */}
            <div className="hidden lg:fixed lg:top-[var(--app-top-offset,0px)] lg:bottom-0 lg:start-0 lg:z-30 lg:flex">{content}</div>

            {/* Placeholder to take up physical space because the real sidebar has `fixed` position. */}
            <div
                style={{
                    paddingInlineStart: MAIN_SIDEBAR_WIDTH,
                }}
                className="invisible hidden lg:sticky lg:top-0 lg:bottom-0 lg:start-0 lg:block"
            />
        </>
    );
};
